import {GetAudioFilesWithSubpath, GetImageFilesWithSubpath} from "../../../Store/main/general/CacheState.js";
import {dayInMS} from "web-vcore";
import {EffectPointerPlayer} from "../../../Server/DB/@Shared/EffectPointerPlayer.js";
import {EngineConfig_VR} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_VR.js";
import {Session} from "../../../Server/DB/sessions/@Session.js";
import {GetAnchorStatDayForTargetTime, GetStatNightBeforeStatDay} from "../../../UI/Stats/Helpers.js";
import {GetMyRecentSessions} from "../../../Utils/AutoRuns/AccessorWarmers_RecentSessions.js";
import {PlaySound} from "../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {ImagePathInfo, ParseImagePath} from "../../../Utils/Bridge/Bridge_Native/MediaTypes.js";
import {LiveSession} from "../../LiveSession.js";
import {SessionEvent} from "../SessionEvent.js";
import {AlarmsComp} from "./AlarmsComp.js";
import {EngineSessionComp} from "./EngineSessionComp.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";

export class VRComp extends EngineSessionComp<EngineConfig_VR> {
	constructor(session: LiveSession, config: EngineConfig_VR) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());
	}
	hc: EngineConfig_VR;

	GetTriggerPackages() {
		return [];
	}

	headsetOn_effectPlayer = new EffectPointerPlayer();
	headsetOff_effectPlayer = new EffectPointerPlayer();
	lieCatch_effectPlayer = new EffectPointerPlayer();
	falseAccuse_effectPlayer = new EffectPointerPlayer();

	OnStart() {
		this.headsetOn_effectPlayer.effectPointer = this.c.headsetOn_effect;
		this.headsetOn_effectPlayer.OnStart();
		this.headsetOff_effectPlayer.effectPointer = this.c.headsetOff_effect;
		this.headsetOff_effectPlayer.OnStart();
		this.lieCatch_effectPlayer.effectPointer = this.c.lieCatch_effect;
		this.lieCatch_effectPlayer.OnStart();
		this.falseAccuse_effectPlayer.effectPointer = this.c.falseAccuse_effect;
		this.falseAccuse_effectPlayer.OnStart();
	}
	OnStop() {
		this.headsetOn_effectPlayer.Stop();
		this.headsetOff_effectPlayer.Stop();
		this.lieCatch_effectPlayer.Stop();
		this.falseAccuse_effectPlayer.Stop();
	}

	Suspend() {
		const result = super.Suspend();
		this.headsetOn_effectPlayer.Stop();
		this.headsetOff_effectPlayer.Stop();
		this.lieCatch_effectPlayer.Stop();
		this.falseAccuse_effectPlayer.Stop();
		return result;
	}

	NotifyVRHeadsetState(onHead: boolean) {
		if (onHead) {
			this.headsetOn_effectPlayer.Play();
		} else {
			this.headsetOff_effectPlayer.Play();
		}
	}

	async PlayAudioClipForFrame(frame_poolName: string, frame_texturePath: string, playClipFromAnotherEpisode: boolean) {
		const pool = this.c.frames_pools.find(a=>a.name == frame_poolName);
		if (pool == null) {
			this.Log(`Frame-pool not found: ${frame_poolName}`);
			return;
		}
		const displayedFrame_pathInfo = ParseImagePath(frame_texturePath);
		const audioFiles = GetAudioFilesWithSubpath(this.c.frames_audioFolder).filter(file=>{
			if (pool.mediaPathFilter && !file.filePath.toLowerCase().includes(pool.mediaPathFilter?.toLowerCase())) return false;
			if (pool.mediaPathExclude && file.filePath.toLowerCase().includes(pool.mediaPathExclude?.toLowerCase())) return false;
			return true;
		});
		const getAudioFileForClip = (clip: ImagePathInfo)=>audioFiles.find(a=>{
			if (a.filePathInfo.seriesStr != clip.seriesStr) return false;
			if (a.filePathInfo.seasonStr != clip.seasonStr) return false;
			if (a.filePathInfo.episodeStr != clip.episodeStr) return false;
			return true;
		});

		let targetClip: ImagePathInfo;
		if (playClipFromAnotherEpisode) {
			const clipStartPoints_otherEpisodes = GetImageFilesWithSubpath(this.c.frames_imageFolder).filter(file=>{
				if (pool.mediaPathFilter && !file.filePath.toLowerCase().includes(pool.mediaPathFilter?.toLowerCase())) return false;
				if (pool.mediaPathExclude && file.filePath.toLowerCase().includes(pool.mediaPathExclude?.toLowerCase())) return false;
				// must be from the same series (if for no other reason, because otherwise "detecting the lie" would be way too easy!)
				if (file.filePathInfo.seriesStr != displayedFrame_pathInfo.seriesStr) return false;
				// but, cannot be the same exact episode (ie. series->season->episode)
				if (
					file.filePathInfo.seriesStr == displayedFrame_pathInfo.seriesStr &&
					file.filePathInfo.seasonStr == displayedFrame_pathInfo.seasonStr &&
					file.filePathInfo.episodeStr == displayedFrame_pathInfo.episodeStr
				) return false;
				if (file.filePathInfo.timeStr_asSeconds == null) return false;
				if (getAudioFileForClip(file.filePathInfo) == null) return false;
				return true;
			});
			targetClip = clipStartPoints_otherEpisodes.Random()?.filePathInfo;
			if (targetClip == null) {
				this.Log(`No audio-clip found as lie (ie. from other season/episode) for base image-frame: ${frame_texturePath
					} (series: ${displayedFrame_pathInfo.seriesStr}, season: ${displayedFrame_pathInfo.seasonStr}, episode: ${displayedFrame_pathInfo.episodeStr})`);
				return;
			}
		} else {
			if (displayedFrame_pathInfo.timeStr_asSeconds == null) {
				this.Log(`No time-str found for display frame. @frame_texturePath:${frame_texturePath}`);
				return;
			}
			targetClip = displayedFrame_pathInfo;
		}

		const audioFile = getAudioFileForClip(targetClip);
		if (audioFile == null) {
			this.Log(`No audio-file found matching target-clip, based on target clip's series (${displayedFrame_pathInfo.seasonStr}), season (${displayedFrame_pathInfo.seasonStr}), and episode (${displayedFrame_pathInfo.episodeStr}).`);
			return;
		}

		await PlaySound({entry: audioFile}, {
			volume: pool.audioClip_volumeMultiplier,
			startTime_min: targetClip.timeStr_asSeconds, startTime_max: targetClip.timeStr_asSeconds,
			durationLimit: pool.audioClip_duration,
		});
	}

	// solver
	// ==========

	cycle_liesCaught = 0;
	OnLeavePhase_Drifting(newPhase: Phase): void {
		this.cycle_liesCaught = 0;
	}

	NotifyUserChangedActiveFrame(firstDisplay: boolean): {newCount: number, newTimeCredit: number, newMPPCount: number} {
		if (this.c.solver_enabled) this.TryToSolve(0);
		if (firstDisplay) {
			this.s.AsLocal!.AddEvent({type: "VR.ImageFrameDisplay"});
		}

		const liveSessionAnchorStatDay = GetAnchorStatDayForTargetTime(this.s.coreData.startTime, this.s.coreData.launchType);
		const minFrameStreamingStatDay = this.s.coreData.launchType == "day" ? liveSessionAnchorStatDay : liveSessionAnchorStatDay + dayInMS; // if night session, we actually target the next stat-day
		const minFrameStreamingStatNight = GetStatNightBeforeStatDay(minFrameStreamingStatDay); // also include frames from stat-night before target stat-day (since we want to incentivize night frame-streaming, and previous day's credit-requirements are met before bedtime)
		const recentSessions = GetMyRecentSessions.CatchBail([] as Session[]);
		const sessionsCountedForCredit = recentSessions.filter(a=>{
			if (a.launchType == "day") return a.startTime >= minFrameStreamingStatDay;
			if (a.launchType == "night") return a.startTime >= minFrameStreamingStatNight;
		});

		const frameDisplaysCounted_pastSessions = sessionsCountedForCredit.flatMap(a=>a.events.filter(b=>b.type == "VR.ImageFrameDisplay"));
		const frameDisplaysCounted_liveSession = this.s.AsLocal!.EventsCopy.filter(a=>a.type == "VR.ImageFrameDisplay");
		//KeepVars({recentSessions, sessionsSinceDayStart: sessionsSinceFakeDayStart, frameDisplaysToday_pastSessions, frameDisplaysToday_liveSession});
		const frameDisplaysCounted = [...frameDisplaysCounted_pastSessions, ...frameDisplaysCounted_liveSession];

		const mpPromptsCounted_pastSessions = sessionsCountedForCredit.flatMap(a=>a.events.filter(b=>b.type == "MemoryPalace.PromptPlay"));
		const mpPromptsCounted_liveSession = this.s.AsLocal!.EventsCopy.filter(a=>a.type == "MemoryPalace.PromptPlay");
		const mpPromptsCounted = [...mpPromptsCounted_pastSessions, ...mpPromptsCounted_liveSession];

		const newCount = frameDisplaysCounted.length;
		const newTimeCredit = GetTimeCredit(frameDisplaysCounted, this.c.frames_timeCreditLimitPerFrame * 1000);
		const newMPPCount = mpPromptsCounted.length;
		return {newCount, newTimeCredit, newMPPCount};
	}
	NotifyUserMarkedImageFrameAsLie(userWasCorrect: boolean, imagePath: string|n) {
		if (userWasCorrect) {
			this.cycle_liesCaught++;
			if (this.c.solver_enabled) this.TryToSolve(1);
			this.s.AsLocal!.AddEvent({type: "VR.ImageFrameLieCatch"});
			this.lieCatch_effectPlayer.Play();
		} else {
			/*this.cycle_liesCaught--;
			if (this.c.solver_enabled) this.TryToSolve(-1);*/
			this.s.AsLocal!.AddEvent({type: "VR.ImageFrameFalseAccuse"});
			this.Log(`User marked image-frame as a lie, but they were incorrect. Image path (in case tagging wrong): ${imagePath}`);
			this.falseAccuse_effectPlayer.Play();
		}
	}
	async NotifyUserMarkedImageFrameAsMistagged(imagePath: string|n) {
		this.Log(`User marked image-frame as mistagged. Image path: ${imagePath}`);
		await this.falseAccuse_effectPlayer.Play();
		await this.falseAccuse_effectPlayer.Play();
	}

	TryToSolve(newLiesCaught: number) {
		const alarmsComp = this.s.Comp(AlarmsComp);
		if (this.cycle_liesCaught < this.c.solver_minFlawFinds) {
			if (this.c.solver_markProgressBeforeCompletion) {
				alarmsComp.NotifySolveProgress(this.c.solver_maxTimeForProgress);
			}
			return;
		}

		// If we make it here, then the solving has completed!
		// ==========

		alarmsComp.NotifySolveCompleted();
		this.cycle_liesCaught = 0;
	}
}

/** Time-value returned is in milliseconds. */
function GetTimeCredit(frameDisplayEvents: SessionEvent[], timeCreditLimitPerFrame_ms: number) {
	let timeCredit = 0;
	for (const [i, event] of frameDisplayEvents.entries()) {
		const prevEvent = frameDisplayEvents[i - 1] as SessionEvent|n;
		if (prevEvent == null) continue;
		const timeDiff = event.date - prevEvent.date;
		timeCredit += timeDiff.KeepAtMost(timeCreditLimitPerFrame_ms);
	}
	return timeCredit;
}